/**
 * Build the auto-captured context payload attached to a feedback
 * submission. Strict allow-list: we read named browser globals
 * field-by-field and never spread or copy-from-unknown-source.
 *
 * What we deliberately do NOT capture:
 *   - cookies, document.cookie
 *   - localStorage, sessionStorage
 *   - axios default headers (Authorization, X-CSRF-Token)
 *   - user email (backend already has user_id from the session)
 *
 * We DO capture the raw `route.search` so admins can reconstruct the
 * exact page (which report / filters were active). Query strings in this
 * app carry filters and IDs, not secrets - auth is cookie + CSRF, never
 * the URL. `route.filters` is kept alongside for the curated chip view.
 *
 * Aligned 1:1 with the backend `FeedbackContextIn` Pydantic schema -
 * the backend rejects unknown / oversized fields with 422.
 */

import type { User } from '../services/userApi'
import type { FeedbackContext } from '../types/feedback'
import { getLastRequestId } from '../services/api'
import { getPreviousRoute } from './routeHistory'

interface FranchiseLike {
  fp_code: string
  franchise_name: string
  country: string
}

interface BuildArgs {
  user?: User
  franchise?: FranchiseLike | null
  location: { pathname: string; search: string }
  params?: Record<string, string>
  /**
   * Override for `app.version`. Caller should pass the value fetched
   * from `useAppInfo()` since Vite's build-time `VITE_APP_VERSION`
   * isn't set in our deploy pipeline. Backend re-enriches at save
   * time anyway, but this keeps the "view full context" preview
   * accurate for the user.
   */
  appVersion?: string | null
}

// Allow-listed URL query keys lifted into `route.filters`. Any other
// key on the URL is dropped. Mirrors the filter axes the inspector +
// admin tables actually use - extend deliberately when a new filter
// becomes worth attaching to feedback.
const FILTER_ALLOW_LIST = new Set<string>([
  'status',
  'market',
  'fp_status',
  'region',
  'data_domain',
  'exception_code',
  'date_from',
  'date_to',
  'category',
  'type',
  'impact',
  'fp_code',
  'country',
  'search',
])

const BROWSER_FAMILIES: Array<{ test: RegExp; name: string }> = [
  // Order matters: Edge ships with "Chrome" in its UA, so probe it first.
  { test: /\bEdg\//, name: 'Edge' },
  { test: /\bOPR\//, name: 'Opera' },
  { test: /\bFirefox\//, name: 'Firefox' },
  { test: /\bChrome\//, name: 'Chrome' },
  { test: /\bSafari\//, name: 'Safari' },
]

// Order matters: iPadOS UAs contain "Mac OS X", and Android UAs contain
// "Linux", so test the more-specific platforms first.
const OS_FAMILIES: Array<{ test: RegExp; name: string }> = [
  { test: /iPhone|iPad|iPod/i, name: 'iOS' },
  { test: /Android/i, name: 'Android' },
  { test: /Windows NT/i, name: 'Windows' },
  { test: /Mac OS X/i, name: 'macOS' },
  { test: /Linux/i, name: 'Linux' },
]

function parseBrowserFamily(userAgent: string): string {
  for (const candidate of BROWSER_FAMILIES) {
    if (candidate.test.test(userAgent)) return candidate.name
  }
  return 'Other'
}

function parseOsFamily(userAgent: string): string {
  for (const candidate of OS_FAMILIES) {
    if (candidate.test.test(userAgent)) return candidate.name
  }
  return 'Other'
}

function pickAllowedFilters(search: string): Record<string, string> {
  const params = new URLSearchParams(search || '')
  const out: Record<string, string> = {}
  params.forEach((value, key) => {
    if (FILTER_ALLOW_LIST.has(key) && value && value.length <= 200) {
      out[key] = value
    }
  })
  return out
}

function resolveEnv(): 'dev' | 'staging' | 'prod' {
  const explicit = (import.meta as any).env?.VITE_ENV as string | undefined
  if (explicit === 'dev' || explicit === 'staging' || explicit === 'prod') {
    return explicit
  }
  if ((import.meta as any).env?.PROD) return 'prod'
  if ((import.meta as any).env?.DEV) return 'dev'
  return 'dev'
}

function resolveVersion(override?: string | null): string {
  if (override && override.trim()) return override.trim()
  return String((import.meta as any).env?.VITE_APP_VERSION || '0.0.0')
}

function resolveSha(): string {
  const sha = (import.meta as any).env?.VITE_BUILD_SHA
    || (import.meta as any).env?.VITE_GIT_SHA
    || 'unknown'
  return String(sha).slice(0, 40)
}

export function buildFeedbackContext(args: BuildArgs): FeedbackContext {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''
  const language = typeof navigator !== 'undefined'
    ? (navigator.language || 'en-US').slice(0, 20)
    : 'en-US'
  const viewportW = typeof window !== 'undefined' ? window.innerWidth || 0 : 0
  const viewportH = typeof window !== 'undefined' ? window.innerHeight || 0 : 0
  const timezone = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone.slice(0, 64)
    } catch {
      return 'UTC'
    }
  })()

  return {
    fp_code: args.franchise?.fp_code ?? null,
    fp_name: args.franchise?.franchise_name ?? null,
    country: args.franchise?.country ?? null,
    route: {
      pathname: args.location.pathname.slice(0, 255),
      search: (args.location.search || '').slice(0, 1024),
      params: args.params || {},
      filters: pickAllowedFilters(args.location.search),
      referrer: getPreviousRoute(),
    },
    browser: {
      family: parseBrowserFamily(ua),
      os: parseOsFamily(ua),
      viewport_w: viewportW,
      viewport_h: viewportH,
      language,
    },
    app: {
      version: resolveVersion(args.appVersion),
      sha: resolveSha(),
      env: resolveEnv(),
    },
    timestamp: new Date().toISOString(),
    timezone,
    correlation_id: getLastRequestId(),
    entry_referrer:
      typeof document !== 'undefined' && document.referrer
        ? document.referrer.slice(0, 1024)
        : null,
  }
}

/**
 * Short-form chip text used in the drawer's "We'll attach: ..." hint.
 * Leads with bits the user wouldn't think to type (browser/OS + build),
 * then adds `this page` and FP code when relevant.
 *
 * The frontend only knows what Vite injected at build time; the
 * server enriches `app.*` + IP after submit. We deliberately omit
 * "build" from the chip line when the value looks like a default so
 * we don't show "build unknown" / "build 0.0.0" to the user.
 */
export function summariseContextForUser(ctx: FeedbackContext): string {
  const segments: string[] = []
  if (ctx.route?.pathname) segments.push('this page')
  if (ctx.fp_code) segments.push(ctx.fp_code)
  if (ctx.browser?.family && ctx.browser?.os) {
    segments.push(`${ctx.browser.family} on ${ctx.browser.os}`)
  }
  if (isMeaningfulSha(ctx.app?.sha)) {
    segments.push(`build ${ctx.app.sha.slice(0, 7)}`)
  }
  return segments.join(' · ')
}

export function isMeaningfulSha(sha: string | undefined | null): sha is string {
  if (!sha) return false
  if (sha === 'unknown') return false
  if (/^0+(\.0+)*$/.test(sha)) return false  // 0.0.0 placeholder
  return true
}
